import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Sentry } from "@/lib/sentry";
import { ReserveHeader } from "@/components/ReserveHeader";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/sentry-test")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Sentry Test — Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: SentryTestPage,
});

function BoomComponent() {
  // Intentional render-time error to verify Sentry captures React errors with
  // readable stack traces in production via uploaded source maps.
  throw new Error("Sentry render test: BoomComponent threw on render");
  // eslint-disable-next-line no-unreachable
  return null;
}

function SentryTestPage() {
  const [renderBoom, setRenderBoom] = useState(false);

  const captureManual = () => {
    const id = Sentry.captureException(
      new Error(`Sentry manual test (admin) @ ${new Date().toISOString()}`),
      { level: "error", tags: { test: "manual_admin_button" } },
    );
    toast.success(`Captured event ${id ?? "(no id — Sentry disabled here)"}`);
  };

  const throwAsync = () => {
    setTimeout(() => {
      throw new Error(`Sentry async test (admin) @ ${new Date().toISOString()}`);
    }, 0);
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <ReserveHeader />
      <main className="max-w-3xl mx-auto px-6 py-12 space-y-8">
        <header>
          <p className="tracking-wide-luxury text-accent text-xs">Admin · Diagnostics</p>
          <h1 className="font-display text-3xl mt-2">Sentry test</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-prose">
            These buttons deliberately raise errors so you can confirm Sentry is
            ingesting events and the configured alert rule emails you. Sentry is
            only enabled on the production hostname (<code>thehospitalityreserve.com</code>),
            so run these on the live site — not on preview.
          </p>
        </header>

        <section className="border border-border bg-card p-6 space-y-4">
          <h2 className="font-display text-xl">1 · Manual capture</h2>
          <p className="text-sm text-muted-foreground">
            Sends a tagged exception via <code>Sentry.captureException</code>.
            Safe — does not crash the page.
          </p>
          <Button onClick={captureManual} className="rounded-none">
            Send test exception
          </Button>
        </section>

        <section className="border border-border bg-card p-6 space-y-4">
          <h2 className="font-display text-xl">2 · Uncaught async error</h2>
          <p className="text-sm text-muted-foreground">
            Throws inside <code>setTimeout</code>; the browser global handler
            forwards it to Sentry. The page keeps running.
          </p>
          <Button onClick={throwAsync} variant="outline" className="rounded-none">
            Throw async error
          </Button>
        </section>

        <section className="border border-destructive/40 bg-card p-6 space-y-4">
          <h2 className="font-display text-xl">3 · Render-time crash</h2>
          <p className="text-sm text-muted-foreground">
            Renders a component that throws during render. This is the truest
            test of source-map upload — the stack trace in Sentry should show
            <code> BoomComponent </code> with the original line numbers.
            Reload the page afterwards to recover.
          </p>
          <Button
            onClick={() => setRenderBoom(true)}
            variant="destructive"
            className="rounded-none"
          >
            Crash this page
          </Button>
          {renderBoom ? <BoomComponent /> : null}
        </section>

        <section className="border border-border bg-card p-6 space-y-2 text-sm text-muted-foreground">
          <h2 className="font-display text-xl text-foreground">Verifying alerts</h2>
          <ol className="list-decimal pl-5 space-y-1">
            <li>Open this page on the published live site signed in as an admin.</li>
            <li>Click each button above.</li>
            <li>Open Sentry → Issues. You should see new events with readable stack frames.</li>
            <li>If your alert rule is "A new issue is created" with environment = production, you should receive an email within ~1 minute.</li>
          </ol>
        </section>
      </main>
    </div>
  );
}
